import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loginDestination } from '@/lib/auth-navigation'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { UserProfile, LoginRequest } from '@/types'
import toast from 'react-hot-toast'

interface TenantOption {
  slug: string
  name: string
}

interface AuthContextType {
  user: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean
  availableTenants: TenantOption[]
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshProfile: () => Promise<void>
  switchTenant: (tenantSlug: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [availableTenants, setAvailableTenants] = useState<TenantOption[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const isAuthenticated = !!user

  // Load user profile on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (token) {
      loadProfile()
    } else {
      setIsLoading(false)
    }
  }, [])

  const loadProfile = async () => {
    try {
      const response = await authApi.getProfile()
      setUser(response.data)
      // Load available tenants (silently — non-critical)
      try {
        const tenantsResp = await authApi.myTenants()
        setAvailableTenants(tenantsResp.data.tenants)
      } catch {
        // ignore — switcher just won't show
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authApi.login(credentials)
      const { access_token, refresh_token } = response.data

      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)

      await loadProfile()
      
      toast.success('Login efetuado com sucesso!')
      navigate(loginDestination(location.search))
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
      setAvailableTenants([])
      queryClient.clear()
      navigate('/login')
      toast.success('Sessão terminada')
    }
  }

  const switchTenant = async (tenantSlug: string) => {
    try {
      const response = await authApi.switchTenant(tenantSlug)
      const { access_token, refresh_token } = response.data
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      // Full reload garante que React Query cache e todo o estado são limpos
      window.location.href = '/dashboard'
    } catch (error) {
      console.error('Switch tenant failed:', error)
      toast.error('Não foi possível trocar de tenant')
      throw error
    }
  }

  const refreshProfile = async () => {
    await loadProfile()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        availableTenants,
        login,
        logout,
        refreshProfile,
        switchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
